import { NgTemplateOutlet } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Router } from '@angular/router';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { AuthService } from '../../../../shared/services/apis/usuario/auth.service';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { CrmEtapaOportunidade } from '../../../../shared/services/models/vendas/crm-etapa-oportunidade.model';
import { CrmEtapasKanbanService } from '../../../../shared/services/apis/vendas/crm-etapas-kanban.service';
import { CrmEtapasKanbanRequest } from '../../../../shared/services/requests/vendas/crm-etapas-kanban.request';
import {
  EtapaOportunidadeModalComponent,
  EtapaOportunidadeModalResult,
} from './dialogs/etapa-oportunidade-modal/etapa-oportunidade-modal.component';

@Component({
  selector: 'app-config-kanban-oportunidades',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    HeaderComponent,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatDialogModule,
    DragDropModule,
    EmptyListComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './config-kanban-oportunidades.component.html',
  styleUrls: ['./config-kanban-oportunidades.component.css'],
})
export class ConfigKanbanOportunidadesComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;

  /**
   * O fluxo é exibido em dois blocos, como no Kanban de Compras: só o principal é um
   * cdkDropList, então uma etapa customizada não pode ser arrastada para depois da etapa
   * final — a final não participa da reordenação.
   */
  public etapasPrincipais: CrmEtapaOportunidade[] = [];
  public etapasFinais: CrmEtapaOportunidade[] = [];
  public _isLoading = false;

  /** Ordem real do fluxo: o endpoint de reordenação exige a lista completa da empresa. */
  public get etapas(): CrmEtapaOportunidade[] {
    return [...this.etapasPrincipais, ...this.etapasFinais];
  }

  /** Id da etapa criada na sessão atual do modal, para o retry atualizar em vez de recriar. */
  private idEtapaCriadaNoModal = 0;

  public readonly tooltipEtapaPadrao =
    'Esta é uma etapa fixa do fluxo e não pode ser excluída, apenas renomeada.';

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private alertService: AlertService,
    private authService: AuthService,
    private crmEtapasKanbanService: CrmEtapasKanbanService
  ) {}

  ngOnInit(): void {
    // A tela é restrita ao master (story, "Localização no sistema"). A API é quem garante
    // isso de fato; aqui só evitamos renderizar uma tela que só devolveria erro.
    if (!this.authService.getUserInfos()?.master) {
      this.alertService.show(
        'Atenção',
        'Apenas o usuário master pode configurar o Kanban de oportunidades.',
        AlertType.warning
      );
      this.voltar();
      return;
    }

    this.carregarEtapas();
  }

  // ----------------------------------------------------------------------
  // Resumo (barra de estatísticas)
  // ----------------------------------------------------------------------
  public get totalEtapasFluxo(): number {
    return this.etapasPrincipais.length;
  }

  public get totalEtapasFinais(): number {
    return this.etapasFinais.length;
  }

  public get labelEtapasFinais(): string {
    return this.totalEtapasFinais === 1 ? 'etapa final' : 'etapas finais';
  }

  public get totalCamposConfigurados(): number {
    return this.etapas.reduce((acc, etapa) => acc + (etapa.qtdCampos ?? 0), 0);
  }

  // ----------------------------------------------------------------------
  // Reordenação
  // ----------------------------------------------------------------------
  public drop(event: CdkDragDrop<CrmEtapaOportunidade[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    // Só o fluxo principal é reordenável: a etapa final fica sempre no fim.
    moveItemInArray(this.etapasPrincipais, event.previousIndex, event.currentIndex);
    this.renumerar();
    this.salvarOrdenacao();
  }

  private renumerar(): void {
    this.etapas.forEach((etapa, index) => (etapa.ordem = index + 1));
  }

  /**
   * O endpoint exige a lista completa da empresa e recusa qualquer troca de posição
   * entre etapas padrão — por isso manda todos os ids na ordem atual da tela.
   */
  private salvarOrdenacao(): void {
    const idsOrdenados = this.etapas.map(etapa => etapa.idEtapaKanban);
    if (idsOrdenados.length === 0) return;

    this.crmEtapasKanbanService.reordenar({ idsOrdenados }).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show(
            'Atenção',
            response.errorDescription || 'Não foi possível salvar a ordenação.',
            AlertType.warning
          );
          // A tela ficou fora de sincronia com o banco: recarrega a ordem real.
          this.carregarEtapas();
          return;
        }

        this.aplicarEtapas(response.listaEtapas ?? []);
      },
      error: () => {
        this.alertService.show('Erro', 'Não foi possível salvar a ordenação.', AlertType.error);
        this.carregarEtapas();
      },
    });
  }

  // ----------------------------------------------------------------------
  // Ações
  // ----------------------------------------------------------------------
  public toggleAtivo(etapa: CrmEtapaOportunidade, ativo: boolean): void {
    // A atribuição vem antes do diálogo de propósito: o [checked] só reescreve o switch se o
    // valor ligado mudar, então reverter exige que ele tenha sido alterado primeiro.
    etapa.isAtivo = ativo;

    if (ativo) {
      this.persistirAtivo(etapa, true);
      return;
    }

    // A API recusa inativar etapa com oportunidades: o aviso já adianta quantas precisam ser movidas.
    const qtdCards = etapa.qtdCards ?? 0;
    const impacto =
      qtdCards > 0
        ? `Há ${qtdCards} oportunidade(s) nesta etapa, que precisam ser movidas para outra etapa antes da inativação.`
        : 'No momento não há oportunidades nesta etapa.';

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Inativar etapa',
        description: `A etapa "${etapa.nome}" deixará de aparecer no board. ${impacto} Confirma a inativação?`,
        buttonConfirm: 'Inativar etapa',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) {
        etapa.isAtivo = true;
        return;
      }

      this.persistirAtivo(etapa, false);
    });
  }

  /** Ativar e inativar pelo PUT da etapa, com isAtivo (molde da compra). */
  private persistirAtivo(etapa: CrmEtapaOportunidade, ativo: boolean): void {
    this.crmEtapasKanbanService
      .update(etapa.idEtapaKanban, this.toRequest({ ...etapa, isAtivo: ativo }))
      .subscribe({
        next: response => {
          if (response.isError) {
            etapa.isAtivo = !ativo;
            // A recusa de inativar traz um impedimento por linha; o alerta renderiza HTML.
            this.alertService.show(
              'Atenção',
              (response.errorDescription || 'Não foi possível atualizar a etapa.').split('\n').join('<br>'),
              AlertType.warning
            );
            return;
          }

          this.alertService.show(
            ativo ? 'Etapa ativada' : 'Etapa inativada',
            ativo
              ? `A etapa "${etapa.nome}" voltou ao board.`
              : `A etapa "${etapa.nome}" foi ocultada do board.`,
            AlertType.success
          );
          this.carregarEtapas();
        },
        error: () => {
          etapa.isAtivo = !ativo;
          this.alertService.show('Erro', 'Não foi possível atualizar a etapa.', AlertType.error);
        },
      });
  }

  public novaEtapa(): void {
    this.abrirModalEtapa(null);
  }

  public editarEtapa(etapa: CrmEtapaOportunidade): void {
    this.abrirModalEtapa(etapa);
  }

  private abrirModalEtapa(etapa: CrmEtapaOportunidade | null): void {
    // Zera o id de criação da sessão anterior do modal (ver salvarEtapaDoModal).
    this.idEtapaCriadaNoModal = 0;

    const dialogRef = this.dialog.open(EtapaOportunidadeModalComponent, {
      width: '780px',
      maxWidth: '96vw',
      autoFocus: false,
      panelClass: 'etapa-oportunidade-dialog',
      data: {
        etapa,
        todasEtapas: this.etapas,
        onSalvar: (dados: EtapaOportunidadeModalResult) => this.salvarEtapaDoModal(dados),
      },
    });

    dialogRef.afterClosed().subscribe((result?: EtapaOportunidadeModalResult) => {
      if (!result) return;

      if (result.excluiu && result.etapa) {
        // O botão "Excluir etapa" do modal já é a confirmação explícita do usuário;
        // exclui direto, sem abrir um segundo diálogo de confirmação.
        this.executarExclusao(result.etapa);
        return;
      }

      if (result.salvou) this.carregarEtapas();
    });
  }

  private salvarEtapaDoModal(dados: EtapaOportunidadeModalResult): Observable<boolean> {
    const salva = { ...dados.etapa };
    // O id vem daqui, e não de `dados.etapa`: o modal remonta o payload a cada clique, então
    // gravar o id nele se perdia e o retry (após falha nas sub-configurações) repetia o POST,
    // que o backend recusa com "Já existe uma etapa do kanban com esse nome".
    const idExistente = salva.idEtapaKanban || this.idEtapaCriadaNoModal;
    const criando = dados.isNova && !idExistente;
    // Nova etapa entra imediatamente antes da etapa final (CA04).
    if (criando) salva.ordem = this.ordemNovaEtapa;

    const requisicao = criando
      ? this.crmEtapasKanbanService.create(this.toRequest(salva))
      : this.crmEtapasKanbanService.update(idExistente, this.toRequest({ ...salva, idEtapaKanban: idExistente }));

    return requisicao.pipe(
      switchMap(response => {
        const idEtapa = criando ? response.etapa?.idEtapaKanban : idExistente;
        if (response.isError || !idEtapa) {
          // A recusa de inativar traz um impedimento por linha; o alerta renderiza HTML.
          this.alertService.show(
            'Atenção',
            (response.errorDescription || 'Não foi possível salvar a etapa.').split('\n').join('<br>'),
            AlertType.warning
          );
          return of(false);
        }

        if (criando) this.idEtapaCriadaNoModal = idEtapa;
        dados.etapa.idEtapaKanban = idEtapa;

        return this.persistirSubConfiguracoesDoModal(
          idEtapa,
          dados,
          `Etapa "${salva.nome}" ${criando ? 'criada' : 'atualizada'} com sucesso.`
        ).pipe(switchMap(ok => (ok && criando ? this.posicionarNovaEtapa(idEtapa) : of(ok))));
      }),
      catchError(() => {
        this.alertService.show('Erro', 'Não foi possível salvar a etapa.', AlertType.error);
        return of(false);
      })
    );
  }

  private persistirSubConfiguracoesDoModal(
    idEtapaKanban: number,
    dados: EtapaOportunidadeModalResult,
    mensagemSucesso: string
  ): Observable<boolean> {
    const chamadas = [
      this.crmEtapasKanbanService.salvarCampos(idEtapaKanban, { campos: dados.campos }),
      this.crmEtapasKanbanService.salvarPermissoes(idEtapaKanban, { permissoes: dados.permissoes }),
    ];

    // A etapa final não admite movimentações de saída e o endpoint recusa a chamada
    // inteira — enviar mesmo uma lista vazia faria o save de Encerrado falhar sempre.
    if (!dados.etapa.isFinal) {
      chamadas.push(
        this.crmEtapasKanbanService.salvarMovimentacoes(idEtapaKanban, { movimentacoes: dados.movimentacoes })
      );
    }

    return forkJoin(chamadas).pipe(
      map(respostas => {
        const erro = respostas.find(resposta => resposta.isError);
        if (erro) {
          this.alertService.show(
            'Atenção',
            erro.errorDescription || 'Não foi possível salvar todas as configurações da etapa.',
            AlertType.warning
          );
          return false;
        }

        this.alertService.show('Sucesso', mensagemSucesso, AlertType.success);
        return true;
      }),
      catchError(() => {
        this.alertService.show(
          'Erro',
          'Não foi possível salvar todas as configurações da etapa.',
          AlertType.error
        );
        return of(false);
      })
    );
  }

  /**
   * O POST só acrescenta a etapa no fim: sem isto ela nasce com a mesma ordem da etapa
   * final e o desempate por nome a joga depois de "Encerrado". Reordena logo após a
   * criação para cumprir o CA04 (nova etapa imediatamente antes da final).
   */
  private posicionarNovaEtapa(idNova: number): Observable<boolean> {
    const naoFinais = this.etapasPrincipais.map(e => e.idEtapaKanban);
    const finais = this.etapasFinais.map(e => e.idEtapaKanban);
    if (finais.length === 0) return of(true);

    return this.crmEtapasKanbanService.reordenar({ idsOrdenados: [...naoFinais, idNova, ...finais] }).pipe(
      // isError vem em HTTP 200: sem checar aqui, a falha mais provável passaria batida.
      map(response => {
        if (response.isError) this.avisarPosicionamentoFalhou();
        return true;
      }),
      catchError(() => {
        this.avisarPosicionamentoFalhou();
        return of(true);
      })
    );
  }


  private avisarPosicionamentoFalhou(): void {
    this.alertService.show(
      'Atenção',
      'A etapa foi criada, mas não foi possível posicioná-la antes da etapa final. Arraste-a para a posição desejada.',
      AlertType.warning
    );
  }

  public excluirEtapa(etapa: CrmEtapaOportunidade): void {
    if (etapa.isFixo) {
      this.alertService.show('Atenção', this.tooltipEtapaPadrao, AlertType.warning);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar exclusão',
        description: `A etapa "${etapa.nome}" será removida do fluxo, junto com seus campos e movimentações. Confirma a exclusão?`,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;
      this.executarExclusao(etapa);
    });
  }

  /**
   * Exclusão no molde da compra: sem vínculos, a etapa é apagada; com vínculos, a API não altera nada e
   * devolve possuiVinculos com a pergunta. Aceitando, a etapa é inativada (EtapasKanban/inativar). Se a
   * inativação seria recusada (oportunidades na etapa, etapa de retorno da reabertura de outra), a API já
   * responde sem possuiVinculos, com uma mensagem por linha, e a tela só mostra o erro.
   */
  private executarExclusao(etapa: CrmEtapaOportunidade): void {
    if (etapa.isFixo) {
      this.alertService.show('Atenção', this.tooltipEtapaPadrao, AlertType.warning);
      return;
    }

    this.crmEtapasKanbanService.delete(etapa.idEtapaKanban).subscribe({
      next: response => {
        if (response.isError && response.possuiVinculos) {
          this.dialog.open(ConfirmModalComponent, {
            maxWidth: '96vw',
            data: {
              title: 'Exclusão não permitida',
              description: response.errorDescription,
              buttonConfirm: 'Inativar etapa',
              buttonClose: 'Cancelar',
            },
          }).afterClosed().subscribe(confirmacao => {
            if (!confirmacao?.confirm) return;
            this.inativarEtapa(etapa);
          });
          return;
        }

        if (response.isError) {
          // A recusa traz um impedimento por linha; o alerta renderiza HTML.
          this.alertService.show(
            'Atenção',
            (response.errorDescription || 'Não foi possível excluir a etapa.').split('\n').join('<br>'),
            AlertType.warning
          );
          return;
        }

        this.alertService.show('Sucesso', 'Etapa excluída com sucesso.', AlertType.success);
        this.carregarEtapas();
      },
      error: () => this.alertService.show('Erro', 'Não foi possível excluir a etapa.', AlertType.error),
    });
  }

  private inativarEtapa(etapa: CrmEtapaOportunidade): void {
    this.crmEtapasKanbanService.inativar(etapa.idEtapaKanban).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show(
            'Atenção',
            (response.errorDescription || 'Não foi possível inativar a etapa.').split('\n').join('<br>'),
            AlertType.warning
          );
          return;
        }

        this.alertService.show('Sucesso', 'Etapa inativada com sucesso.', AlertType.success);
        this.carregarEtapas();
      },
      error: () => this.alertService.show('Erro', 'Não foi possível inativar a etapa.', AlertType.error),
    });
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/vendas']);
  }

  // ----------------------------------------------------------------------
  // Carga
  // ----------------------------------------------------------------------
  private carregarEtapas(): void {
    this._isLoading = true;

    this.crmEtapasKanbanService.getAll('').subscribe({
      next: response => {
        this._isLoading = false;

        if (response.isError) {
          this.alertService.show(
            'Atenção',
            response.errorDescription || 'Não foi possível carregar as etapas.',
            AlertType.warning
          );
          return;
        }

        this.aplicarEtapas(response.listaEtapas ?? []);
      },
      error: () => {
        this._isLoading = false;
        this.alertService.show(
          'Erro',
          'Não foi possível carregar as etapas do kanban de oportunidades.',
          AlertType.error
        );
      },
    });
  }

  private aplicarEtapas(etapas: CrmEtapaOportunidade[]): void {
    const normalizadas = etapas
      .map(etapa => this.normalizarEtapa(etapa))
      .sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome));

    this.etapasPrincipais = normalizadas.filter(etapa => !etapa.isFinal);
    this.etapasFinais = normalizadas.filter(etapa => etapa.isFinal);
  }

  private normalizarEtapa(etapa: CrmEtapaOportunidade): CrmEtapaOportunidade {
    const cor = etapa.cor || '#1E5DB8';
    return {
      ...etapa,
      descricao: etapa.descricao ?? '',
      cor,
      tint: etapa.tint || `${cor}22`,
      icone: etapa.icone || 'description',
      isFixo: etapa.isFixo ?? false,
      qtdCampos: etapa.qtdCampos ?? 0,
      qtdDestinos: etapa.qtdDestinos ?? 0,
      qtdCards: etapa.qtdCards ?? 0,
    };
  }

  /** Nova etapa entra imediatamente antes da etapa final (CA04). */
  private get ordemNovaEtapa(): number {
    return this.etapasPrincipais.length + 1;
  }

  private toRequest(etapa: CrmEtapaOportunidade): CrmEtapasKanbanRequest {
    return {
      chave: etapa.chave || null,
      nome: etapa.nome,
      descricao: etapa.descricao || null,
      cor: etapa.cor || null,
      icone: etapa.icone || null,
      ordem: etapa.ordem,
      isOpcional: etapa.isOpcional,
      isAtivo: etapa.isAtivo,
      isEspecial: etapa.isEspecial,
      isFinal: etapa.isFinal,
      idEtapaKanbanRetornoReabertura: etapa.idEtapaKanbanRetornoReabertura ?? null,
    };
  }
}
